import { useMemo, useState } from 'react'
import { Alert, App, Button, Card, Select, Space, Switch, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { UrlSearchInput } from '@/shared/ui/UrlSearchInput'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { RowActions } from '@/shared/ui/table/RowActions'
import { NamedCode } from '@/features/notify/NamedCode'
import { eventName, templateName } from '@/features/notify/notifyLabels'
import { SubscriptionFormModal } from '@/features/notify/SubscriptionFormModal'
import {
  CHANNELS,
  deleteSubscription,
  fetchSubscriptions,
  updateSubscription,
  type SubscriptionView,
} from '@/features/notify/notifyApi'

/**
 * Obunalar — qaysi hodisa qaysi kanal(lar) orqali, qanday ustuvorlik bilan
 * xabar beradi. `priority` kichik raqam = yuqori ustuvorlik: masalan bir
 * xil `eventType` uchun `TELEGRAM priority=0`, `SMS priority=1` bo'lsa,
 * dispatch avval Telegram'ni sinaydi, u yetib bormasa (bog'lanmagan) SMS'ga
 * tushadi (`notify/README.md` "Kanal tanlash tartibi").
 *
 * Ro'yxat `eventType` bo'yicha saralab ko'rsatiladi — bir hodisaning butun
 * fallback zanjiri yonma-yon ko'rinsin.
 */
export function NotifySubscriptionsPage() {
  const { t } = useTranslation()
  /*
    ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm`
    `ConfigProvider` mavzusiga yetmaydi va qorong'i panel ustida OQ
    dialog chizadi (o'lchov `SettingsPage` dagi izohda).
  */
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<SubscriptionView | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /*
    ⚠️ SAHIFALASH OLIB TASHLANDI (JADVAL-QOIDASI §6).

    `GET /api/notify/subscriptions` (`SubscriptionResource#list`) faqat
    ixtiyoriy `eventType` ni biladi — `page`/`size` YO'Q va javob TO'LIQ
    ro'yxat. Ilgari bu yerda `pagination={{ current, pageSize }}` turardi,
    ya'ni server hammasini yuborar, panel esa MIJOZDA kesardi va
    "sahifalash bor" degan soxta taassurot berardi.

    ⚠️ Shu sababdan tartib raqami ham endi oddiy `index + 1`: siljish
    hisoblanadigan sahifa yo'q.

    ⚠️ Qidiruv va filtr ham MIJOZDA, lekin ular TO'LIQ ro'yxat ustidan
    yuradi — hech qanday qator yashirinmaydi. Holat MANZILDA.
  */
  const { state: tableState, patch: patchTable, reset: resetTable } = useTableUrlState()
  const channelFilter = tableState.filters['channel']?.[0]
  const enabledFilter = tableState.filters['enabled']?.[0]
  const query = tableState.search.trim().toLowerCase()

  const subscriptions = useQuery({
    queryKey: queryKeys.notify.subscriptions,
    queryFn: fetchSubscriptions,
  })

  const all = useMemo(
    () =>
      [...(subscriptions.data ?? [])].sort(
        (a, b) => a.eventType.localeCompare(b.eventType) || a.priority - b.priority,
      ),
    [subscriptions.data],
  )

  const sorted = useMemo(
    () =>
      all.filter((row) => {
        if (channelFilter !== undefined && row.channel !== channelFilter) return false
        if (enabledFilter === 'on' && !row.enabled) return false
        if (enabledFilter === 'off' && row.enabled) return false
        if (query === '') return true
        return [row.eventType, row.templateCode, eventName(row.eventType, t) ?? '', templateName(row.templateCode, t) ?? '']
          .some((text) => text.toLowerCase().includes(query))
      }),
    [all, channelFilter, enabledFilter, query, t],
  )

  const isFiltered = query !== '' || channelFilter !== undefined || enabledFilter !== undefined

  const toggleEnabled = useMutation({
    mutationFn: (row: SubscriptionView) =>
      updateSubscription(row.id, { priority: row.priority, enabled: !row.enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notify.subscriptions })
      // X-SIB-004 — kalit o'z holicha ham o'zgaradi, lekin u SERVER
      // qabul qilganini bildirmaydi: so'rov yiqilsa ham kalit siljigan
      // ko'rinardi. Tasdiq aynan javob kelgandan keyin chiqadi.
      void message.success(t('common.toastSaved'))
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteSubscription(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notify.subscriptions })
      void message.success(t('common.toastDeleted'))
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const columns: ColumnsType<SubscriptionView> = [
    {
      // Sahifalash yo'q — `index` allaqachon to'liq ro'yxatdagi o'rin
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'eventType',
      title: t('notify.subscription.eventType'),
      width: 220,
      render: (_, row) => <NamedCode name={eventName(row.eventType, t)} code={row.eventType} />,
    },
    {
      key: 'priority',
      title: t('notify.subscription.priority'),
      width: 90,
      align: 'center',
      render: (_, row) => <Tag>{row.priority}</Tag>,
    },
    {
      // ⚠️ Voronka jadval USTIGA chiqarildi — sabab
      //    `NotifyTemplatesPage` dagi bir xil ustun izohida.
      key: 'channel',
      title: t('notify.channel'),
      width: 130,
      render: (_, row) => t(`notify.channelName.${row.channel}`),
    },
    {
      key: 'templateCode',
      title: t('notify.subscription.templateCode'),
      width: 220,
      render: (_, row) => <NamedCode name={templateName(row.templateCode, t)} code={row.templateCode} />,
    },
    {
      key: 'enabled',
      title: t('notify.subscription.enabled'),
      width: 110,
      render: (_, row) => (
        /*
          ⚠️ O'CHIRISH TASDIQ SO'RAYDI, YOQISH — YO'Q.

          Assimetriya ataylab: yoqish xabar oqimini TIKLAYDI (xatosi
          ko'rinadi), o'chirish esa uni JIMGINA to'xtatadi. O'chirilgan
          obuna hech qanday xato bermaydi — paket tugashi, bloklash va
          balans xabarlari shunchaki yubormay qo'yiladi va buni birinchi
          bo'lib abonent internetsiz qolganda sezadi.

          Matn AYNAN shuni aytadi: qaysi hodisa, qaysi kanal.
        */
        <Can permission="notify.subscription.write" fallback={<Switch checked={row.enabled} disabled />}>
          <Switch
            checked={row.enabled}
            loading={toggleEnabled.isPending}
            onChange={() => {
              if (!row.enabled) {
                toggleEnabled.mutate(row)
                return
              }
              modal.confirm({
                title: t('notify.subscription.disableTitle'),
                content: t('notify.subscription.disableConfirm', {
                  eventType: eventName(row.eventType, t) ?? row.eventType,
                  channel: t(`notify.channelName.${row.channel}`),
                }),
                okButtonProps: { danger: true },
                okText: t('notify.subscription.disable'),
                cancelText: t('common.cancel'),
                onOk: () => toggleEnabled.mutateAsync(row),
              })
            }}
          />
        </Can>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('tariffs.edit'),
              onClick: () => {
                setEditing(row)
                setFormOpen(true)
              },
              permission: 'notify.subscription.write',
            },
            {
              key: 'delete',
              label: t('actions.delete'),
              onClick: () => {
                modal.confirm({
                  title: t('notify.subscription.deleteTitle'),
                  content: t('notify.subscription.deleteConfirm', { eventType: eventName(row.eventType, t) ?? row.eventType }),
                  okButtonProps: { danger: true },
                  okText: t('actions.delete'),
                  cancelText: t('common.cancel'),
                  onOk: () => remove.mutateAsync(row.id),
                })
              },
              permission: 'notify.subscription.write',
              danger: true,
              divider: true,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.notifySubscriptions')}
        description={t('pageIntro.notifySubscriptions')}
        extra={
          <Can permission="notify.subscription.write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('notify.subscription.createTitle')}
            </Button>
          </Can>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/*
        ⚠️ BO'SH RO'YXAT — BU "MA'LUMOT YO'Q" EMAS, OQIBATI BOR HOLAT.

        Obuna bo'lmasa tizim HECH KIMGA, HECH QACHON xabar yubormaydi:
        paket tugashi, bloklash, balans kamayishi — hammasi jimgina
        o'tadi. Abonent internetsiz qolganda buni faqat o'zi sezadi.

        Standart "Ma'lumot topilmadi" yozuvi buni yashiradi: operator
        "hali hech kim obuna bo'lmagan, tabiiy" deb o'ylaydi. Shuning
        uchun oqibat ochiq yoziladi.

        ⚠️ Yuklanish tugaguncha ko'rsatilmaydi — aks holda har ochilganda
        bir zumga "xabar yuborilmaydi" chaqnab, keyin yo'qolardi.

        ⚠️ FILTR QO'YILGANDA ham ko'rsatilmaydi va bu MUHIM: filtr hech
        narsa topmagani "obuna yo'q" degani EMAS. Aks holda bitta
        kanalni tanlash butun tizim jim qolgandek ogohlantirish
        chiqarardi — va operator uni bir haftada umuman o'qimay
        qo'yardi.

        ⚠️ Xato bo'lganda ham ko'rsatilmaydi: so'rov yiqilgani ham
        "obuna yo'q" degani emas.
      */}
      {!subscriptions.isPending && !subscriptions.isError && !isFiltered && all.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message={t('notify.subscription.emptyWarning')}
          description={t('notify.subscription.emptyWarningHint')}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {subscriptions.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('dataTable.loadFailed')}
          description={translateError(subscriptions.error, t)}
        />
      ) : null}

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <UrlSearchInput placeholder={t('notify.subscription.searchPlaceholder')} />
          <Select
            allowClear
            style={{ minWidth: 170 }}
            placeholder={t('notify.channel')}
            value={channelFilter}
            onChange={(v?: string) =>
              patchTable({ filters: { ...tableState.filters, channel: v ? [v] : [] } })
            }
            options={CHANNELS.map((c) => ({ value: c, label: t(`notify.channelName.${c}`) }))}
          />
          {/* ⚠️ "Faqat o'chirilganlar" — jim qolgan hodisalarni topish
              yo'li. Usiz ular uzun ro'yxatda ko'zga tashlanmaydi. */}
          <Select
            allowClear
            style={{ minWidth: 190 }}
            placeholder={t('notify.subscription.enabled')}
            value={enabledFilter}
            onChange={(v?: string) =>
              patchTable({ filters: { ...tableState.filters, enabled: v ? [v] : [] } })
            }
            options={[
              { value: 'on', label: t('notify.subscription.onlyEnabled') },
              { value: 'off', label: t('notify.subscription.onlyDisabled') },
            ]}
          />
          {isFiltered ? (
            <Space size={4}>
              <Tag color="blue">
                {t('roles.shownOfTotal', { shown: sorted.length, total: all.length })}
              </Tag>
              <Button size="small" type="link" onClick={resetTable}>
                {t('dataTable.clearFilters')}
              </Button>
            </Space>
          ) : null}
        </Space>

        <Table<SubscriptionView>
          columns={columns}
          dataSource={sorted}
          rowKey={(row) => row.id}
          loading={subscriptions.isPending}
          size="middle"
          scroll={{ x: 1000 }}
          // ⚠️ SAHIFALASH YO'Q — sabab yuqoridagi blok izohida
          pagination={false}
          locale={{
            emptyText: (
              <EmptyState
                variant="no-data"
                title={
                  isFiltered ? t('dataTable.emptyFiltered') : t('notify.subscription.emptyWarning')
                }
                description={
                  isFiltered
                    ? t('dataTable.emptyFilteredHint')
                    : t('notify.subscription.emptyWarningHint')
                }
                {...(isFiltered
                  ? { actionLabel: t('dataTable.resetFilters'), onAction: resetTable }
                  : {})}
              />
            ),
          }}
        />
      </Card>

      <SubscriptionFormModal
        open={formOpen}
        subscription={editing}
        onClose={() => setFormOpen(false)}
      />
    </>
  )
}
